import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTopButton from '@/components/base/ScrollToTop';
import { articles, faqItems, testimonials } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function HomeSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '黄油游戏官方指南：玩法攻略与资源大全',
    description: '黄油游戏是深受玩家喜爱的休闲互动类游戏，本页汇集黄油游戏完整玩法教程、角色攻略、隐藏资源与新手入门技巧。',
    url: 'https://butter-game-guide.readdy.ai/',
    isPartOf: {
      '@type': 'WebSite',
      name: '黄油游戏官方指南',
      url: 'https://butter-game-guide.readdy.ai/',
    },
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

function FaqSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showAllArticles, setShowAllArticles] = useState(false);

  useEffect(() => {
    document.title = '黄油游戏官方指南：玩法攻略与资源大全';
  }, []);

  const featuredArticles = articles.filter((a) => a.featured);
  const latestArticles = articles.filter((a) => !a.featured);
  const visibleLatest = showAllArticles ? latestArticles : latestArticles.slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <HomeSchema />
      <FaqSchema />
      <Navbar />
      <ScrollToTopButton />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20game%20world%20landscape%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20rolling%20hills%20with%20game%20elements%2C%20soft%20cream%20sky%20with%20decorative%20clouds%2C%20whimsical%20game%20aesthetic%2C%20no%20text&width=1800&height=900&seq=hero-bg-home-01&orientation=landscape"
              alt="黄油游戏世界"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center py-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-white/20 text-white text-sm mb-6">
              <i className="ri-sparkling-line"></i>
              <span>{t('site.tagline')}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white font-heading mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/category/all"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('hero.cta')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/category/beginner"
                className="px-8 py-3.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold text-sm hover:bg-white/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('hero.secondary')}
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {t('section.categories')}
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                {t('section.categories.desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.filter((c) => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1">{cat.name}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {t('section.featured')}
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                {t('section.featured.desc')}
              </p>
            </div>
            <div className="space-y-6">
              {featuredArticles.slice(0, 1).map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featuredArticles.slice(1).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  {t('section.latest')}
                </h2>
                <p className="text-foreground-600">{t('section.latest.desc')}</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleLatest.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            {latestArticles.length > 3 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowAllArticles(!showAllArticles)}
                  className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {showAllArticles ? '收起' : '查看更多攻略'}
                  {showAllArticles ? <i className="ri-arrow-up-s-line ml-1"></i> : <i className="ri-arrow-down-s-line ml-1"></i>}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                玩家口碑
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                来自黄油游戏玩家的真实反馈
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-semibold text-sm">
                      {item.name.charAt(item.name.length - 1)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {t('section.faq')}
              </h2>
              <p className="text-foreground-600">{t('section.faq.desc')}</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer whitespace-nowrap"
                  >
                    <span className="font-medium text-foreground-900 pr-4">{item.question}</span>
                    {openFaq === index ? (
                      <i className="ri-arrow-up-s-line text-foreground-500 flex-shrink-0"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-foreground-500 flex-shrink-0"></i>
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}